import React, { useMemo } from "react";
import classNames from "classnames";
import { PopoverPosition } from "@blueprintjs/core";
import { FieldArray } from "react-final-form-arrays";
import { get } from "lodash";
import { SpaceviewerElementFormField } from "pages/RS-Workspace/components/Results/components/ElementDialog";
import Button from "components/Button";
import ParameterRow from "../ParameterRow";
import ParameterRowPreview from "../ParameterRowPreview";
import { defaultParameter } from "../..";
import { getFieldRestrictions, getRequestName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { ResultElementSourceType, } from "store/rs-workspace/rs-workspace.types";
import { isParameterDimensionCompleted } from "store/rs-workspace/utils/completeness.utils";
import PlusIcon from "icons/plus.svg";
import * as styles from "../../styles.module.scss";
const MAX_PARAMETERS = 8;
const Parameters = ({ elementData, editMode, restrictions = {}, upsert, remove }) => {
    const parameters = useMemo(() => get(elementData, SpaceviewerElementFormField.parameters), [elementData]);
    const sources = useMemo(() => {
        const sources = get(elementData, SpaceviewerElementFormField.sources);
        if (editMode) {
            return sources;
        }
        return sources.map(({ source, view }) => {
            if (source?.type === ResultElementSourceType.Insight) {
                return { ...source, isFinal: source.output.isFinal, outputId: source.output.id, view };
            }
            return { ...source, simulationId: source?.id, view };
        });
    }, [editMode, elementData]);
    const addParameterButtonDisabled = useMemo(() => parameters.some((parameter) => !isParameterDimensionCompleted(parameter)) ||
        parameters?.length >= MAX_PARAMETERS, [parameters]);
    const addButtonTitle = useMemo(() => {
        if (!addParameterButtonDisabled) {
            return "Add Parameter";
        }
        if (parameters?.length >= MAX_PARAMETERS) {
            return `Maximum number of parameters is ${MAX_PARAMETERS}.`;
        }
        return "Complete existing parameter.";
    }, [addParameterButtonDisabled, parameters?.length]);
    const parametersRestrictions = useMemo(() => getFieldRestrictions(restrictions, [getRequestName(SpaceviewerElementFormField.parameters)])
        ?.SpaceViewElementParameter, [restrictions]);
    return (React.createElement("div", { className: styles.parameters },
        React.createElement("div", { className: styles.header },
            React.createElement("div", { className: classNames(styles.cell, styles.parameter) },
                "Parameter",
                parametersRestrictions?.Parameter?.Parameter?.ParameterName?.IsMandatory ? " *" : ""),
            React.createElement("div", { className: classNames(styles.cell, styles.type) }, "Type"),
            React.createElement("div", { className: classNames(styles.cell, styles.value) }, "Value"),
            React.createElement("div", { className: classNames(styles.cell, styles.unit) },
                "Units",
                parametersRestrictions?.Parameter?.Parameter?.Units?.IsMandatory ? " *" : ""),
            React.createElement("div", { className: classNames(styles.cell, styles.range) }, "Range"),
            React.createElement("div", { className: classNames(styles.cell, styles.granularity) }, "Granularity"),
            editMode ? (React.createElement(Button, { label: React.createElement(PlusIcon, { width: 25, height: 25 }), square: true, position: PopoverPosition.RIGHT, onClick: () => upsert(defaultParameter), disabled: addParameterButtonDisabled, showTooltipIfDisabled: true, title: addButtonTitle, classes: {
                    root: classNames(styles.addParameter, {
                        [styles.disabled]: addParameterButtonDisabled,
                    }),
                } })) : (React.createElement("div", { className: classNames(styles.cell, styles.delete) }))),
        React.createElement("div", { className: styles.body }, editMode ? (React.createElement(FieldArray, { name: `${SpaceviewerElementFormField.parameters}` }, ({ fields }) => fields.map((name, index) => (React.createElement(ParameterRow, { key: name, name: name, parameter: fields.value[index], parameters: parameters, sources: sources, restrictions: parametersRestrictions, remove: remove, upsert: upsert }))))) : (parameters.map((parameter, index) => (React.createElement(ParameterRowPreview, { key: index, parameter: parameter, sources: sources })))))));
};
export default Parameters;
